import { z } from 'zod'
import { oc } from '@orpc/contract'

/**
 * The visit reasons one provider supports, labelled — the dropdown behind booking.
 *
 * Exists because neither half of the answer is available on its own:
 *
 * - `GET /v1/providers` gives the provider's `visit_reason_ids` but **no names** (our own two
 *   providers return 435 and 808 opaque `pc_…` ids);
 * - `GET /v1/visit_reasons` gives names but for Zocdoc's whole catalogue, and **may 403** — no
 *   reference-data scope is listed on our token.
 *
 * So this intersects them, narrowing the reference read to the provider's own specialties rather
 * than walking the catalogue. When the reference read is refused, the ids still come back with
 * `labelsAvailable: false` — an unlabelled reason is still bookable, and the caller can fall back
 * to the `zocdoc_mapping` crosswalk for the ones it knows.
 */
export const zocdocVisitReasons = oc
  .route({ method: 'GET', path: '/v2/zocdoc/visit-reasons' })
  .input(
    z.object({
      /** The practitioner's NPI. Resolved to Zocdoc's `pr_…` inside the handler. */
      npi: z.string().min(1),
      /**
       * Keep only these ids — pass a slot's `allowedVisitReasonIds` to get exactly the reasons that
       * slot is bookable for. Omitted or empty = every reason the provider supports.
       */
      onlyIds: z.array(z.string()).optional(),
    }),
  )
  .output(
    z.object({
      /**
       * In label order when labelled (so the dropdown reads alphabetically), otherwise in the order
       * Zocdoc listed them.
       */
      reasons: z.array(
        z.object({
          id: z.string(),
          /** Absent = Zocdoc gave no name for this id; the caller should show the id itself. */
          name: z.string().optional(),
          specialtyId: z.string().optional(),
        }),
      ),
      /** Reasons the provider supports in total, before `onlyIds` narrowed the list. */
      supportedCount: z.number(),
      /** How many of `reasons` came back without a name. */
      unlabelledCount: z.number(),
      /** False when the reference read was refused — every reason is then unlabelled. */
      labelsAvailable: z.boolean(),
      /**
       * Why labels are missing, when they are. Not an error: the list is still usable, and this is
       * how the 403-on-a-missing-scope case explains itself instead of looking like a bug.
       */
      labelWarning: z.string().optional(),
      error: z.string().optional(),
      missingEnv: z.array(z.string()).optional(),
    }),
  )
